import { useState } from "react";
import CalComponent from "./CalComponent";

const DemoForm = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    businessEmail: "",
    jobTitle: "",
    phone: "",
    phoneCode: "+91",
    demoTime: "",
    description: "",
  });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(true);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    if (!formData.demoTime) {
      setError("Please select a demo time before submitting.");
      setIsSubmitting(false);
      return;
    }

    try {
      const bookingData = {
        eventTypeId: 12345,
        startTime: new Date(formData.demoTime).toISOString(),
        endTime: new Date(
          new Date(formData.demoTime).getTime() + 30 * 60000
        ).toISOString(),
        attendees: [
          {
            email: formData.businessEmail,
            name: `${formData.firstName} ${formData.lastName}`,
          },
        ],
        title: `Demo for ${formData.companyName}`,
      };

      const bookingResponse = await fetch(
        "https://prod.toxboxai.com/api/calcom/book",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(bookingData),
        }
      );

      if (!bookingResponse.ok) {
        const errorData = await bookingResponse.json();
        throw new Error(errorData.error || "Failed to create Cal.com booking");
      }

      const bookingResult = await bookingResponse.json();
      const bookingReference = bookingResult.data?.bookingUid || null;

      const formPayload = {
        ...formData,
        demoTime: new Date(formData.demoTime).toISOString(),
        bookingReference,
      };

      const response = await fetch(
        "https://prod.toxboxai.com/api/demo",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formPayload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to submit demo form");
      }

      const result = await response.json();
      setError(null);
      alert("Form submitted and demo scheduled successfully!");
      setFormData({
        firstName: "",
        lastName: "",
        companyName: "",
        businessEmail: "",
        jobTitle: "",
        phone: "",
        phoneCode: "+91",
        demoTime: "",
        description: "",
      });
    } catch (error) {
      console.error("Submission failed:", error);
      setError(error.message || "Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      margin: "0 auto",
      padding: "40px 20px",
      backgroundImage:
        "url('https://imgs.search.brave.com/96cR9aupmr2ytnVojIlP-D2ReoKk-j26EMhZB1l6hic/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9zdGF0/aWMudmVjdGVlenku/Y29tL3N5c3RlbS9y/ZXNvdXJjZXMvdGh1/bWJuYWlscy8wNDcv/NzUzLzIzMi9zbWFs/bC9wdXJwbGUtdGV4/dHVyZWQtYmFja2dy/b3VuZC1mb3ItcHJv/ZmVzc2lvbmFsLXBy/ZXNlbnRhdGlvbnMt/cGhvdG8uanBn')",
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundColor: "#1a1a1a", // Fallback color
      minHeight: "100vh",
      color: "#e0e0e0",
    },
    toggleContainer: {
      display: "flex",
      justifyContent: "center",
      gap: "1rem",
      marginBottom: "30px",
      marginTop: "3rem",
    },
    toggleButton: {
      padding: "12px 24px",
      backgroundColor: "#3b3b3b",
      color: "#e0e0e0",
      border: "none",
      borderRadius: "8px",
      fontSize: "16px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "background-color 0.3s, opacity 0.3s",
      opacity: showForm ? 0.6 : 1,
    },
    toggleButtonActive: {
      backgroundColor: "#0d47a1",
      opacity: 1,
    },
    formContainer: {
      width: "100%",
      padding: "40px",
      backgroundColor: "rgba(42, 42, 42, 0.95)",
      borderRadius: "16px",
      boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
    },
    formTitle: {
      fontSize: "28px",
      fontWeight: "700",
      color: "#4a90e2",
      marginBottom: "20px",
      textAlign: "center",
    },
    formDescription: {
      fontSize: "16px",
      color: "#e0e0e0",
      marginBottom: "24px",
      lineHeight: "1.6",
      textAlign: "center",
    },
    form: {
      display: "grid",
      gap: "1.5rem",
    },
    inputGroup: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "1.5rem",
    },
    fullWidth: {
      gridColumn: "1 / -1",
    },
    input: {
      width: "100%",
      padding: "14px",
      border: "1px solid #4a4a4a",
      borderRadius: "8px",
      fontSize: "15px",
      backgroundColor: "#3b3b3b",
      color: "#e0e0e0",
      transition: "border-color 0.3s, box-shadow 0.3s",
    },
    textarea: {
      width: "100%",
      padding: "14px",
      border: "1px solid #4a4a4a",
      borderRadius: "8px",
      fontSize: "15px",
      backgroundColor: "#3b3b3b",
      color: "#e0e0e0",
      minHeight: "120px",
      resize: "vertical",
      transition: "border-color 0.3s, box-shadow 0.3s",
    },
    select: {
      padding: "14px",
      border: "1px solid #4a4a4a",
      borderRadius: "8px",
      fontSize: "15px",
      backgroundColor: "#3b3b3b",
      color: "#e0e0e0",
      width: "100%",
    },
    note: {
      fontSize: "13px",
      color: "#a0a0a0",
      marginTop: "10px",
      marginBottom: "20px",
      lineHeight: "1.4",
      textAlign: "center",
    },
    button: {
      width: "100%",
      padding: "16px",
      backgroundColor: "#4a90e2",
      color: "#e0e0e0",
      border: "none",
      borderRadius: "8px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: isSubmitting ? "not-allowed" : "pointer",
      opacity: isSubmitting ? 0.6 : 1,
      transition: "background-color 0.3s, opacity 0.3s",
      boxShadow: "0 3px 10px rgba(0,0,0,0.3)",
    },
    dateLabel: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#e0e0e0",
      marginBottom: "8px",
    },
    error: {
      color: "#ff6b6b",
      fontSize: "14px",
      marginBottom: "12px",
      gridColumn: "1 / -1",
      textAlign: "center",
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.toggleContainer}>
        <button
          style={{
            ...styles.toggleButton,
            ...(showForm ? styles.toggleButtonActive : {}),
          }}
          onClick={() => setShowForm(true)}
        >
          Form
        </button>
        <button
          style={{
            ...styles.toggleButton,
            ...(!showForm ? styles.toggleButtonActive : {}),
          }}
          onClick={() => setShowForm(false)}
        >
          Calendar
        </button>
      </div>
      {showForm ? (
        <div style={styles.formContainer}>
          <h3 style={styles.formTitle}>Request a Custom Demo</h3>
          <p style={styles.formDescription}>
            Schedule a personalized demo to explore how Toxbox's AI-powered
            solutions can transform your customer support. Provide your details
            below to help us tailor the experience to your business needs.
          </p>
          <form onSubmit={handleSubmit} style={styles.form}>
            {error && <p style={styles.error}>{error}</p>}
            <div style={styles.fullWidth}>
              <p style={styles.dateLabel}>Select Demo Time*</p>
              <input
                type="datetime-local"
                name="demoTime"
                value={formData.demoTime}
                onChange={handleChange}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.inputGroup}>
              <div>
                <p style={styles.dateLabel}>First Name*</p>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="First Name"
                  style={styles.input}
                  required
                />
              </div>
              <div>
                <p style={styles.dateLabel}>Last Name*</p>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Last Name"
                  style={styles.input}
                  required
                />
              </div>
            </div>
            <div style={styles.fullWidth}>
              <p style={styles.dateLabel}>Company Name*</p>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Your Company Name"
                style={styles.input}
                required
              />
            </div>
            <div style={styles.inputGroup}>
              <div>
                <p style={styles.dateLabel}>Business Email*</p>
                <input
                  type="email"
                  name="businessEmail"
                  value={formData.businessEmail}
                  onChange={handleChange}
                  placeholder="Your Business Email"
                  style={styles.input}
                  required
                />
              </div>
              <div>
                <p style={styles.dateLabel}>Job Title*</p>
                <input
                  type="text"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleChange}
                  placeholder="Your Job Title"
                  style={styles.input}
                  required
                />
              </div>
            </div>
            <div style={styles.inputGroup}>
              <div>
                <p style={styles.dateLabel}>Phone Code</p>
                <select
                  name="phoneCode"
                  value={formData.phoneCode}
                  onChange={handleChange}
                  style={styles.select}
                >
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                </select>
              </div>
              <div>
                <p style={styles.dateLabel}>Phone Number</p>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your Phone Number"
                  style={styles.input}
                />
              </div>
            </div>
            <div style={styles.fullWidth}>
              <p style={styles.dateLabel}>Description</p>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Briefly describe your needs or goals for the demo"
                style={styles.textarea}
              />
            </div>
            <p style={styles.note}>
              By registering, you confirm that you agree to the storing and
              processing of your personal data by Toxbox as described in the
              Privacy Statement.
            </p>
            <button type="submit" style={styles.button} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit and Schedule Demo"}
            </button>
          </form>
        </div>
      ) : (
        <CalComponent />
      )}
    </div>
  );
};

export default DemoForm;